import { Box, Flex, Text, type Icon } from "@chakra-ui/react";
import { type BrandColor } from "contexts/color-theme";
import React from "react";
import { getBrandColorHex } from "theme/colors";
import { Link } from "ui/link";
import { cardHoverTranslation } from "utils";

type JumpLinkCardProps = {
  icon: typeof Icon;
  iconColor: BrandColor;
  title: string;
  href: string;
};

export function JumpLinkCard({ icon, iconColor, title, href }: JumpLinkCardProps) {
  const focusRingColor = getBrandColorHex("blue", 950);

  return (
    /**
     * Parent element is necessary to avoid a layout shift when hovering over the bottom of the card.
     * See `cardHoverTranslation` for more details.
     */
    <Box className="group" boxSize="100%">
      <Link
        variant="unstyled"
        className="group"
        aria-label={`Jump to ${title}`}
        href={href}
        borderRadius={{ base: "small", lg: "bigger" }}
        boxSize="100%"
        _groupHover={{
          "& .icon-container": { bg: `new.${iconColor}.150`, color: `new.${iconColor}.900` },
          ...cardHoverTranslation,
        }}
        transition="transform 0.15s ease"
      >
        <Flex
          boxSize="100%"
          align="center"
          bg="brand.background.light"
          borderRadius={{ base: "small", lg: "bigger" }}
          boxShadow="card"
          gap="smaller"
          justify="center"
          flexDirection={{ base: "column", lg: "row" }}
          p={{ base: "small", lg: "big" }}
          textAlign={{ base: "center", lg: "left" }}
          _groupFocusVisible={{ boxShadow: `inset 0px 0px 0px 2px ${focusRingColor}` }}
        >
          <Flex
            className="icon-container"
            align="center"
            boxSize={{ base: "bigger", lg: "biggest" }}
            borderRadius={{ base: "tiny", lg: "smaller" }}
            bg="new.gray.150"
            color="brand.text.light"
            flexShrink={0}
            justify="center"
            transition="all 0.15s ease-in-out"
          >
            <Box as={icon} boxSize={{ base: "icon.md", lg: "icon.lg" }} />
          </Flex>
          <Text textStyle={{ base: "caption2", lg: "caption1" }} fontWeight="semibold">
            {title}
          </Text>
        </Flex>
      </Link>
    </Box>
  );
}
